import * as angular from "angular";
import marked from "marked";
import type { SchemaMessageVariant } from "./schemaUiMessages";
import componentTemplate from "./schema-markdown.html";
import decoratorTemplate from "./schema-markdown-field.html";
import "./schema-markdown.less";

export function isSafeMarkdownLink(href: string): boolean {
	// Keep the allowed schemes and malformed-link handling aligned with mobile.
	const hasUnsafeCharacters = typeof href !== "string" || Array.from(href).some(character => {
		const code = character.charCodeAt(0);
		return code <= 32 || code === 127 || character === "\\";
	});
	return !hasUnsafeCharacters
		&& /^(https?:\/\/[^/?#]+(?:[/?#].*)?|mailto:[^/?#]+(?:\?.*)?|tel:\+?[\d().-]+)$/i.test(href);
}

export function renderSchemaMarkdown(content: string, sanitize: (html: string) => string): string {
	const renderer = new marked.Renderer();
	const renderLink = renderer.link.bind(renderer);
	renderer.link = (href, title, text) => isSafeMarkdownLink(href ?? "")
		? renderLink(href, title, text).replace("<a ", '<a target="_blank" rel="noopener noreferrer" ')
		: text;
	// Omit raw HTML and images from fixed Markdown guidance.
	renderer.html = () => "";
	renderer.image = () => "";
	return sanitize(marked(content, { renderer }));
}

export interface SchemaMarkdownController {
	content?: string;
	variant?: SchemaMessageVariant;
}

// AngularJS 1.5 assigns bindings before invoking the constructor.
// eslint-disable-next-line no-redeclare
export class SchemaMarkdownController {
	public static $inject = ["$sanitize"];
	public html = "";
	public variantLabel = "";

	public constructor(private readonly $sanitize: (html: string) => string) {}

	public $onChanges(): void {
		this.html = typeof this.content === "string" && this.content.trim()
			? renderSchemaMarkdown(this.content, this.$sanitize)
			: "";
		this.variantLabel = {
			info: "Information",
			success: "Success",
			warning: "Warning",
			error: "Error"
		}[this.variant ?? ""] ?? "";
	}
}

export function schemaMarkdownAddOn(schemaFormDecoratorsProvider, sfBuilderProvider): void {
	schemaFormDecoratorsProvider.defineAddOn(
		"bootstrapDecorator",
		"markdown",
		"./schema-markdown-field.html",
		[sfBuilderProvider.builders.sfField, sfBuilderProvider.builders.condition]
	);
}

export default angular
	.module("vesl.extensions.json-schema.markdown", ["schemaForm", "ngSanitize"])
	.run(["$templateCache", function($templateCache) {
		$templateCache.put("./schema-markdown-field.html", decoratorTemplate);
	}])
	.config(schemaMarkdownAddOn)
	.component("schemaMarkdown", {
		bindings: { content: "<", variant: "<?" },
		controller: SchemaMarkdownController,
		template: componentTemplate
	})
	.name;
